﻿import ajaxPartials from 'Furillo/modules/ajax-partials.js';
import { extend, formattedNumber } from 'Furillo/modules/helpers.js';
// Full https://www.chartjs.org/ library
//import Chart from 'Vendor/chart.js/auto/auto.js';
import { Chart, LineController, LineElement, PointElement, CategoryScale, LinearScale, Decimation, Filler, Tooltip, DoughnutController, ArcElement } from 'Vendor/chart.js/dist/chart.js';

export default class EsemQuarterlyPrice {

    constructor(element, options) {

        const defaults = {};

        this.element = element;
        this.options = extend(defaults, options);
        this.priceData = null;
        this.chart = null;
        this.doughnutChart = null;
        this.colors = [
            {red: 154, green: 253, blue: 87},
            {red: 255, green: 205, blue: 86},
            {red: 253, green: 64, blue: 82}
        ];

        this.init();
    }

    initMainChart() {
        const self = this;

        if (!self.priceData)
            return;

        //console.log(self.priceData);

        const canvas = self.element.querySelector('canvas[id^="chart-main"]');

        let lineGradient = {
            gradient: null,
            chartSize: {
                width: null,
                height: null
            }
        };

        let bgGradient = {
            gradient: null,
            chartSize: {
                width: null,
                height: null
            }
        };
        
        const chartAreaSize = function (chartArea) {
            return {
                width: chartArea.right - chartArea.left,
                height: chartArea.bottom - chartArea.top
            };
        }

        const getGradient = function (ctx, chartArea, opacity) {
            const size = chartAreaSize(chartArea);

            let gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            gradient.addColorStop(0, 'rgba(' + self.colors[0].red + ', ' + self.colors[0].green + ', ' + self.colors[0].blue + ', ' + opacity + ')');
            gradient.addColorStop(0.5, 'rgba(' + self.colors[1].red + ', ' + self.colors[1].green + ', ' + self.colors[1].blue + ', ' + opacity + ')');
            gradient.addColorStop(1, 'rgba(' + self.colors[2].red + ', ' + self.colors[2].green + ', ' + self.colors[2].blue + ', ' + opacity + ')');

            return { gradient: gradient, chartSize: size };
        };

        const getLineGradient = function (ctx, chartArea) {
            const size = chartAreaSize(chartArea);
            if (lineGradient.gradient && lineGradient.chartSize.width === size.width && lineGradient.chartSize.height === size.height)
                return lineGradient.gradient;

            lineGradient = getGradient(ctx, chartArea, 1);

            return lineGradient.gradient;
        };

        const getBackgroundGradient = function (ctx, chartArea) {
            const size = chartAreaSize(chartArea);
            if (bgGradient.gradient && bgGradient.chartSize.width === size.width && bgGradient.chartSize.height === size.height)
                return bgGradient.gradient;

            bgGradient = getGradient(ctx, chartArea, .2);

            return bgGradient.gradient;
        };

        const data = {
            datasets: [{
                label: 'öre/kWh',
                data: self.priceData.Prices,
                parsing: {
                    xAxisKey: 'ChartAxisLabel',
                    yAxisKey: 'PriceAdjusted'
                },
                fill: true,
                stepped: 'before',
                pointStyle: 'circle',
                pointBorderWidth: 2,
                pointRadius: 6,
                pointHoverRadius: 10,
                hoverBorderWidth: 4,
                hoverBorderColor: 'rgba(40, 40, 40, .8)',
                borderColor: function (context) {
                    const { ctx, chartArea } = context.chart;

                    if (!chartArea) {
                        // This case happens on initial chart load
                        return;
                    }
                    return getLineGradient(ctx, chartArea);
                },
                backgroundColor: function (context) {
                    const { ctx, chartArea } = context.chart;

                    if (!chartArea) {
                        // This case happens on initial chart load
                        return;
                    }
                    return getBackgroundGradient(ctx, chartArea);
                },
            }]
        };

        const ticks = self.priceData.IsQuarterlyPrices ? 16 : 4;

        const config = {
            type: 'line',
            data: data,
            options: {
                responsive: true,
                aspectRatio: 2,
                maintainAspectRatio: true,
                scales: {
                    x: {
                        ticks: {
                            // For a category axis, the val is the index so the lookup via getLabelForValue is needed
                            callback: function (val, index) {
                                return index % ticks === 0 || index === (self.priceData.Prices.length - 1) ? this.getLabelForValue(val) : '';
                            }
                        }
                    }
                },
                interaction: {
                    intersect: false,
                    mode: 'index',
                },
                plugins: {
                    tooltip: {
                        usePointStyle: true,
                    }
                },
                onHover: (e) => {
                    if (e.type !== 'mousemove')
                        return;

                    const points = e.chart.getElementsAtEventForMode(e, 'index', { intersect: false }, true);
                    if (points.length !== 1)
                        return;

                    const item = e.chart.data.datasets[points[0].datasetIndex].data[points[0].index];
                    if (!item)
                        return;                   

                    self.updateDougnutChart(item);                
                }
            }
        };

        self.chart = new Chart(canvas, config);

        self.selectPoint(self.priceData.CurrentDeliveryPrice.ChartAxisLabel);
    }

    selectPoint(label) {
        const self = this;

        const activeIndex = self.getItemIndex(label);
        if (activeIndex < 0)
            return;

        self.chart.setActiveElements([{ datasetIndex: 0, index: activeIndex }]);

        const tooltip = self.chart.tooltip;
        const chartArea = self.chart.chartArea;
        tooltip.setActiveElements([
            {
                datasetIndex: 0,
                index: activeIndex,
            }
        ],
            {
                x: (chartArea.left + chartArea.right) / 2,
                y: (chartArea.top + chartArea.bottom) / 2,
            });

        self.chart.update();
    }

    getItemIndex(label) {
        const self = this;

        if (!self.chart)
            return -1;

        if (!label || label.length === 0)
            return -1;

        const dataset = self.chart.data.datasets[0];
        return dataset.data.findIndex((item) => item.ChartAxisLabel === label);
    }

    updateDougnutChart(item) {
        const self = this;

        if (!self.priceData || !item || !self.doughnutChart)
            return;

        // Calculate
        const maxValue = self.priceData.HighestPriceAdjusted;
        if (maxValue === 0)
            return;

        const circumference = 360;        
        const value = Math.abs(item.PriceAdjusted / maxValue) * circumference;

        self.doughnutChart.data.datasets[0].data = [value, (circumference - value)];
        self.doughnutChart.update();

        self.element.querySelector('div[id^="chart-doughnut-label"] span').textContent = item.PriceAdjustedFormatted;
        self.element.querySelector('div[id^="chart-doughnut-label"] small').textContent = item.Label;
    }

    initDoughnutChart() {
        const self = this;

        if (!self.priceData)
            return;                
        
        const gradientColor = function(fadeFraction, rgbColor1, rgbColor2, rgbColor3) {
            var color1 = rgbColor1;
            var color2 = rgbColor2;
            var fade = fadeFraction;

            // Do we have 3 colors for the gradient? Need to adjust the params.
            // Find which interval to use and adjust the fade percentage
            if (rgbColor3 && fade >= 1) {
                fade -= 1;
                color1 = rgbColor2;
                color2 = rgbColor3;
            }

            var diffRed = color2.red - color1.red;
            var diffGreen = color2.green - color1.green;
            var diffBlue = color2.blue - color1.blue;

            var gradient = {
                red: parseInt(Math.floor(color1.red + (diffRed * fade)), 10),
                green: parseInt(Math.floor(color1.green + (diffGreen * fade)), 10),
                blue: parseInt(Math.floor(color1.blue + (diffBlue * fade)), 10),
            };
            return 'rgb(' + gradient.red + ',' + gradient.green + ',' + gradient.blue + ')';
        }

        const canvas = self.element.querySelector('canvas[id^="chart-doughnut"]');
        const data = {            
            datasets: [{
                data: [0, 360],
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea)
                        return null;

                    if (context.dataIndex === 0 && self.priceData.HighestPriceAdjusted !== 0) {
                        const gradientFade = Math.abs((context.parsed - self.priceData.LowestPriceAdjusted) / self.priceData.HighestPriceAdjusted);                        

                        return gradientColor(gradientFade, self.colors[0], self.colors[1], self.colors[2]);
                    } else {
                        return 'rgb(242, 242, 242)';
                    }
                }
            }]
        };

        const config = {
            type: 'doughnut',            
            data: data,
            options: {
                cutout: '80%',
                plugins: {
                    tooltip: {
                        enabled: false,
                    }
                }
            }
        };

        self.doughnutChart = new Chart(canvas, config); 
        self.updateDougnutChart(self.priceData.CurrentDeliveryPrice);
    }

    setPriceData() {
        const self = this;

        const canvas = self.element.querySelector('canvas[data-prices]');
        if (!canvas) {
            console.warn('No canvas with data found, unable to load price data!')
            return;
        }

        const json = canvas.getAttribute('data-prices');
        if (!json || json.length === 0) {
            console.warn('No price data found on canvas element, unable to init chart!')
            return;
        }

        self.priceData = JSON.parse(json);
    }

    init() {
        const self = this;

        // Only requried if Legend has been loaded (explicitely or through 'auto')
        //Chart.defaults.plugins.legend.display = false;

        // Only needed when not using 'auto'
        Chart.register(LineController, LineElement, PointElement, CategoryScale, LinearScale, Decimation, Filler, Tooltip, DoughnutController, ArcElement);        

        const fetchView = function () {

            if (self.chart)
                self.chart.destroy();

            // Modify url based on view state
            const activeTabPanelElement = self.element.querySelector('div[role="tabpanel"].show');
            const toggleVatElement = self.element.querySelector('input[id^="toggle-vat-"]');            

            let url = self.element.getAttribute('data-ajax-url');            
            const urlSegments = url.split('/');

            if (activeTabPanelElement)
                urlSegments[urlSegments.length - 2] = activeTabPanelElement.getAttribute('tabindex');

            if (toggleVatElement)
                urlSegments[urlSegments.length - 1] = toggleVatElement.checked;
            
            url = urlSegments.join('/');
                        
            self.element.setAttribute('data-ajax-url', url);

            ajaxPartials.makeRequest(self.element, () => {

                self.setPriceData();
                self.initMainChart();
                self.initDoughnutChart();

                self.element.querySelector('input[type="checkbox"]').addEventListener('change', event => {                    
                    fetchView();
                });

                self.element.querySelectorAll('div[id^="table-tab-pane"] table tbody tr').forEach(element => {
                    element.addEventListener('mouseover', event => {

                        let label = event.target.getAttribute('data-label');
                        if (!label)
                            label = event.target.parentElement.getAttribute('data-label');

                        if (!label)
                            return;

                        const activeIndex = self.getItemIndex(label);
                        if (activeIndex < 0)
                            return;

                        const item = self.chart.data.datasets[0].data[activeIndex];                        
                        self.updateDougnutChart(item);

                        self.selectPoint(label);
                    });
                });

            });
        };

        fetchView();     

   //     //this.gameGrid is a parent element for the checkboxes
   //     this.gameGrid.addEventListener("touchmove", function (e) {
   //         // get the touch element
   //         var touch = e.touches[0];

   //         // get the DOM element
   //         var checkbox = document.elementFromPoint(touch.clientX, touch.clientY);

   //         // make sure an element was found - some areas on the page may have no elements
   //         if (checkbox) {
   //             // interact with the DOM element
   //             checkbox.checked = !checkbox.checked;
   //         }
   //             ...
			//});
    }
}